import { FileInput as MantineFileInput, FileInputProps } from '@mantine/core';
import { forwardRef } from 'react';
import { useMantineTheme } from '@mantine/core';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedFileInputProps extends FileInputProps {
  gradientFocus?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const FileInput = forwardRef<HTMLButtonElement, ThemedFileInputProps>(
  ({ gradientFocus = true, gradientVariant = 'primary', useColorScheme = true, style, styles, onChange, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradientFocus) {
      return (
        <MantineFileInput
          ref={ref}
          style={style}
          onChange={onChange}
          styles={{
            ...styles,
            label: {
              textAlign: 'left !important' as any,
              ...(styles as any)?.label,
            },
            input: {
              paddingLeft: rest.leftSection ? '40px' : undefined,
              paddingRight: rest.rightSection ? '40px' : undefined,
              cursor: 'pointer',
              ...(styles as any)?.input,
            },
            section: {
              pointerEvents: 'none',
              ...(styles as any)?.section,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineFileInput
        ref={ref}
        onChange={onChange}
        style={
          {
            '--file-input-focus-color': color,
            '--file-input-focus-shadow': strongFocus
              ? `0 0 0 3px ${color}50`
              : `0 0 0 2px ${color}30`,
            ...style,
          } as unknown as React.CSSProperties
        }
        styles={{
          ...styles,
          label: {
            textAlign: 'left !important' as any,
            ...(styles as any)?.label,
          },
          input: {
            paddingLeft: rest.leftSection ? '40px' : undefined,
            paddingRight: rest.rightSection ? '40px' : undefined,
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            cursor: 'pointer',
            '&:focus': {
              borderColor: color,
              boxShadow: strongFocus
                ? `0 0 0 3px ${color}50`
                : `0 0 0 2px ${color}30`,
              outline: strongFocus ? `2px solid ${color}` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within': {
              borderColor: color,
            },
            ...(styles as any)?.input,
          },
          section: {
            pointerEvents: 'none',
            ...(styles as any)?.section,
          },
        }}
        {...rest}
      />
    );
  }
);

FileInput.displayName = 'FileInput';

export default FileInput;
